import { createContext, useContext, useMemo, ReactNode } from "react"
import { useBoard } from "../../models/board"
import { useEntitlements } from "../../models/entitlements"
import { useTierFeatures } from "../../models/tierFeatures"
import { FeatureFlagName, BOARD_LEVEL_FLAGS } from "./featureFlags"
import { useFlags } from "./FeatureFlagProvider"
import { tierFromEntitlements, Tier } from "./useOwnerTier"

interface BoardFlagContextValue {
  isEnabled: (flag: FeatureFlagName) => boolean
  boardTier: Tier
  loading: boolean
}

const BoardFlagContext = createContext<BoardFlagContextValue>({
  isEnabled: () => false,
  boardTier: "free",
  loading: true,
})

export const useBoardFlags = () => useContext(BoardFlagContext)

export function BoardFlagProvider({ boardUid, children }: { boardUid: string; children: ReactNode }) {
  const userFlags = useFlags()
  const [board, boardLoading] = useBoard(boardUid)
  const [ownerEntitlements, entLoading] = useEntitlements(board?.ownerUid)
  const [tierFeatures, tfLoading] = useTierFeatures()

  const boardTier = tierFromEntitlements(ownerEntitlements)
  const loading = boardLoading || entLoading || tfLoading

  const value = useMemo<BoardFlagContextValue>(() => {
    const boardFlags = new Set<string>()
    if (tierFeatures) {
      const tiers: string[] = boardTier === "free" ? ["free"] : ["free", boardTier]
      for (const t of tiers) {
        for (const f of tierFeatures[t] ?? []) boardFlags.add(f)
      }
    }

    return {
      isEnabled: (flag: FeatureFlagName) => {
        if (flag in userFlags.overrides) return userFlags.overrides[flag] === true
        if (BOARD_LEVEL_FLAGS.has(flag)) return boardFlags.has(flag)
        return userFlags.flags[flag] === true
      },
      boardTier,
      loading,
    }
  }, [userFlags.flags, userFlags.overrides, tierFeatures, boardTier, loading])

  return (
    <BoardFlagContext.Provider value={value}>
      {children}
    </BoardFlagContext.Provider>
  )
}
